import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font
} from '@react-pdf/renderer';

// Note: Using Times-Bold (built-in) for PDF WAYFARE branding
// React-PDF has strict font format requirements that often cause issues with external fonts
// Times-Bold provides a professional serif appearance similar to Noto Serif
// The web app will still use Noto Serif from Google Fonts

// ============================================
// SISTEMA DE ESTILOS PROFESIONAL TIPO REVISTA
// ============================================

const colors = {
  // Paleta Principal - Elegante y Cálida
  primary: '#B8A082',      // Beige/Dorado
  secondary: '#8B7355',    // Marrón Tierra
  accent: '#E8D5C4',       // Crema
  dark: '#2C2416',         // Marrón Oscuro

  // Fondos y Superficies
  background: '#FFFFFF',
  cardBg: '#F9F6F2',       // Blanco Cálido
  lightBg: '#FDF9F5',

  // Texto
  textPrimary: '#2C2416',
  textSecondary: '#6B5D52',
  textLight: '#9B8B7E',

  // Acentos de Color
  success: '#4CAF50',
  warning: '#FF9800',
  info: '#2196F3',

  // Challenges
  challenge: '#FFB74D',
  challengeBg: '#FFF8E1'
};

const styles = StyleSheet.create({
  // ========== PÁGINA BASE ==========
  page: {
    backgroundColor: colors.background,
    fontFamily: 'Helvetica',
  },

  pageWithPadding: {
    backgroundColor: colors.background,
    padding: 40,
    fontFamily: 'Helvetica',
  },

  // ========== PORTADA (COVER PAGE) ==========
  coverPage: {
    backgroundColor: colors.dark,
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },

  coverBackgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    objectFit: 'cover',
  },

  coverImageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dark overlay for text readability
  },

  coverContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
  },

  coverTitle: {
    fontSize: 56,
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
    letterSpacing: 4,
    marginBottom: 10,
    textShadow: '2px 2px 8px rgba(0, 0, 0, 0.8)',
  },

  coverSubtitle: {
    fontSize: 24,
    color: '#FFFFFF',
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: 40,
    letterSpacing: 1,
    textShadow: '1px 1px 6px rgba(0, 0, 0, 0.8)',
  },

  coverDates: {
    fontSize: 16,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 8,
    textShadow: '1px 1px 4px rgba(0, 0, 0, 0.8)',
  },

  coverStats: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 30,
    gap: 20,
  },

  coverStatItem: {
    alignItems: 'center',
    paddingHorizontal: 20,
  },

  coverStatValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 4,
    textShadow: '1px 1px 4px rgba(0, 0, 0, 0.8)',
  },

  coverStatLabel: {
    fontSize: 12,
    color: '#FFFFFF',
    letterSpacing: 1,
    textShadow: '1px 1px 4px rgba(0, 0, 0, 0.8)',
  },

  coverDivider: {
    width: 1,
    height: 40,
    backgroundColor: colors.accent,
    opacity: 0.5,
  },

  coverBranding: {
    position: 'absolute',
    bottom: 40,
    left: 0,
    right: 0,
    alignItems: 'center',
  },

  coverBrandText: {
    fontSize: 12,
    color: colors.accent,
    letterSpacing: 2,
  },

  // ========== WAYFARE BRANDING ==========
  wayfareBrand: {
    fontFamily: 'Times-Bold',
    letterSpacing: 2,
  },

  // ========== TARJETAS DE ESTADÍSTICAS ==========
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
    marginBottom: 30,
  },

  statCard: {
    width: '48%',
    backgroundColor: colors.cardBg,
    borderRadius: 12,
    padding: 20,
    borderLeft: `4px solid ${colors.primary}`,
    minHeight: 120,
  },

  statCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },

  statIcon: {
    fontSize: 28,
    marginRight: 12,
  },

  statValue: {
    fontSize: 36,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 4,
  },

  statLabel: {
    fontSize: 12,
    color: colors.textSecondary,
    letterSpacing: 0.5,
    marginBottom: 8,
  },

  statDetail: {
    fontSize: 10,
    color: colors.textLight,
    fontStyle: 'italic',
  },

  // ========== SECCIONES CON TÍTULOS ==========
  section: {
    marginBottom: 30,
    minPresenceAhead: 100, // Keep at least 100pts of space ahead
  },

  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: `2px solid ${colors.accent}`,
  },

  sectionIcon: {
    fontSize: 24,
    marginRight: 10,
  },

  sectionTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: colors.secondary,
    letterSpacing: 0.5,
  },

  sectionSubtitle: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 4,
  },

  // ========== TARJETAS DE CONTENIDO ==========
  contentCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderLeft: `3px solid ${colors.primary}`,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    minPresenceAhead: 80,
    orphans: 2,
    widows: 2,
  },

  cardTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },

  cardTitleIcon: {
    fontSize: 16,
    marginRight: 6,
  },

  cardContent: {
    fontSize: 11,
    color: colors.textSecondary,
    lineHeight: 1.6,
    marginBottom: 8,
    orphans: 2,
    widows: 2,
  },

  cardMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 12,
  },

  cardMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 9,
    color: colors.textLight,
  },

  cardMetaIcon: {
    fontSize: 10,
    marginRight: 4,
  },

  // ========== TIMELINE DE DÍA ==========
  dayHeader: {
    backgroundColor: colors.primary,
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },

  dayHeaderContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  dayNumber: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },

  dayDate: {
    fontSize: 14,
    color: colors.accent,
    marginTop: 2,
  },

  dayStats: {
    flexDirection: 'row',
    gap: 16,
  },

  dayStatItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  dayStatIcon: {
    fontSize: 14,
    marginRight: 4,
    color: colors.accent,
  },

  dayStatText: {
    fontSize: 11,
    color: '#FFFFFF',
  },

  // ========== GALERÍA DE FOTOS ==========
  photoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 12,
  },

  photoWrapper: {
    width: '31%',
    marginBottom: 10,
  },

  photo: {
    width: '100%',
    height: 120,
    maxHeight: 150,
    borderRadius: 6,
    objectFit: 'cover',
    border: `1px solid ${colors.accent}`,
  },

  photoLarge: {
    width: '100%',
    height: 200,
    maxHeight: 250,
    borderRadius: 8,
    objectFit: 'cover',
    marginBottom: 12,
    border: `2px solid ${colors.accent}`,
    wrap: false,
  },

  photoCaption: {
    fontSize: 8,
    color: colors.textLight,
    marginTop: 4,
    textAlign: 'center',
    fontStyle: 'italic',
  },

  // ========== CHALLENGE CARD ==========
  challengeCard: {
    backgroundColor: colors.challengeBg,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderLeft: `3px solid ${colors.challenge}`,
    minPresenceAhead: 80,
    orphans: 2,
    widows: 2,
  },

  challengeBadge: {
    backgroundColor: colors.challenge,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    alignSelf: 'flex-start',
    marginBottom: 10,
  },

  challengeBadgeText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#FFFFFF',
    letterSpacing: 0.5,
  },

  // ========== GRÁFICOS DE PRESUPUESTO ==========
  budgetSection: {
    backgroundColor: colors.cardBg,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
  },

  budgetHeader: {
    marginBottom: 16,
  },

  budgetTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.textPrimary,
    marginBottom: 4,
  },

  budgetSummary: {
    fontSize: 12,
    color: colors.textSecondary,
  },

  progressBar: {
    height: 24,
    backgroundColor: colors.accent,
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 16,
  },

  progressFill: {
    height: '100%',
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingRight: 12,
  },

  progressText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },

  budgetBreakdown: {
    gap: 8,
  },

  budgetItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
  },

  budgetItemLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },

  budgetItemIcon: {
    fontSize: 14,
    marginRight: 8,
  },

  budgetItemText: {
    fontSize: 11,
    color: colors.textSecondary,
  },

  budgetItemValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.primary,
  },

  // ========== HIGHLIGHTS ==========
  highlightBox: {
    backgroundColor: colors.lightBg,
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    borderTop: `4px solid ${colors.primary}`,
  },

  highlightTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.secondary,
    marginBottom: 12,
    letterSpacing: 1,
  },

  highlightList: {
    gap: 8,
  },

  highlightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 6,
  },

  highlightBullet: {
    fontSize: 14,
    marginRight: 8,
    color: colors.primary,
  },

  highlightText: {
    fontSize: 11,
    color: colors.textSecondary,
    flex: 1,
    lineHeight: 1.5,
  },

  // ========== FOOTER ==========
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTop: `1px solid ${colors.accent}`,
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  footerText: {
    fontSize: 8,
    color: colors.textLight,
  },

  pageNumber: {
    fontSize: 8,
    color: colors.textLight,
  },

  // ========== CIUDAD INFO ==========
  cityInfoBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    border: `1px solid ${colors.accent}`,
  },

  cityInfoRow: {
    flexDirection: 'row',
    marginBottom: 12,
  },

  cityInfoIcon: {
    fontSize: 16,
    marginRight: 10,
    color: colors.primary,
    width: 20,
  },

  cityInfoContent: {
    flex: 1,
  },

  cityInfoLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.textSecondary,
    marginBottom: 2,
  },

  cityInfoText: {
    fontSize: 11,
    color: colors.textPrimary,
    lineHeight: 1.5,
  },

  // ========== REFLECTIONS ==========
  reflectionCard: {
    backgroundColor: colors.lightBg,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    borderLeft: `4px solid ${colors.secondary}`,
    minPresenceAhead: 100,
    orphans: 2,
    widows: 2,
  },

  reflectionDayTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.secondary,
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: `1px solid ${colors.accent}`,
  },

  reflectionQuestion: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.textSecondary,
    marginBottom: 6,
    marginTop: 12,
  },

  reflectionAnswer: {
    fontSize: 10,
    color: colors.textPrimary,
    lineHeight: 1.6,
    marginBottom: 4,
    orphans: 2,
    widows: 2,
  },
});

// ============================================
// COMPONENTES DEL PDF
// ============================================

// Portada Estilo Revista
const CoverPage = ({ userData, tripStats, featuredPhoto }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Page size="A4" style={featuredPhoto ? styles.coverPage : { ...styles.coverPage, backgroundColor: colors.dark }}>
      {/* Foto de fondo (si existe) - Order matters! Background first, then overlay, then content */}
      {featuredPhoto && (
        <Image
          src={featuredPhoto}
          style={styles.coverBackgroundImage}
        />
      )}

      {featuredPhoto && <View style={styles.coverImageOverlay} />}

      <View style={styles.coverContent}>
        {/* Título principal */}
        <Text style={styles.coverTitle}>{userData.destination}</Text>

        {/* Subtítulo */}
        <Text style={styles.coverSubtitle}>Travel Journal</Text>

        {/* Fechas */}
        {userData.startDate && userData.endDate && (
          <Text style={styles.coverDates}>
            {formatDate(userData.startDate)} - {formatDate(userData.endDate)}
          </Text>
        )}

        {/* Estadísticas rápidas */}
        <View style={styles.coverStats}>
          <View style={styles.coverStatItem}>
            <Text style={styles.coverStatValue}>{tripStats.duration}</Text>
            <Text style={styles.coverStatLabel}>Days</Text>
          </View>

          <View style={styles.coverDivider} />

          <View style={styles.coverStatItem}>
            <Text style={styles.coverStatValue}>{tripStats.photosCount}</Text>
            <Text style={styles.coverStatLabel}>Photos</Text>
          </View>

          <View style={styles.coverDivider} />

          <View style={styles.coverStatItem}>
            <Text style={styles.coverStatValue}>{tripStats.notesCount}</Text>
            <Text style={styles.coverStatLabel}>Memories</Text>
          </View>
        </View>
      </View>

      {/* Branding */}
      <View style={styles.coverBranding}>
        <Text style={styles.coverBrandText}>
          <Text style={styles.wayfareBrand}>WAYFARE</Text> by LOS DIAS
        </Text>
        <Text style={{ fontSize: 10, color: colors.accent, marginTop: 4 }}>www.losdias.xyz</Text>
      </View>
    </Page>
  );
};

// Página de Resumen con Tarjetas
const SummaryPage = ({ tripStats, userData }) => {
  return (
    <Page size="A4" style={styles.pageWithPadding}>
      {/* Header */}
      <View style={styles.sectionHeader}>
        <View>
          <Text style={styles.sectionTitle}>Trip Overview</Text>
          <Text style={styles.sectionSubtitle}>Your journey in numbers</Text>
        </View>
      </View>

      {/* Tarjetas de estadísticas */}
      <View style={styles.statsGrid}>
        {/* Duración */}
        <View style={styles.statCard}>
          <Text style={styles.statValue}>{tripStats.duration}</Text>
          <Text style={styles.statLabel}>Days</Text>
          <Text style={styles.statDetail}>
            {userData.startDate && new Date(userData.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            {' - '}
            {userData.endDate && new Date(userData.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </Text>
        </View>

        {/* Presupuesto */}
        <View style={styles.statCard}>
          <Text style={styles.statValue}>€{tripStats.totalSpent || 0}</Text>
          <Text style={styles.statLabel}>Total Spent</Text>
          <Text style={styles.statDetail}>{tripStats.budgetUtilization || 0}% of budget</Text>
        </View>

        {/* Memories */}
        <View style={styles.statCard}>
          <Text style={styles.statValue}>{tripStats.notesCount}</Text>
          <Text style={styles.statLabel}>Memories</Text>
          <Text style={styles.statDetail}>Captured moments</Text>
        </View>

        {/* Fotos */}
        <View style={styles.statCard}>
          <Text style={styles.statValue}>{tripStats.photosCount}</Text>
          <Text style={styles.statLabel}>Photos</Text>
          <Text style={styles.statDetail}>Visual memories</Text>
        </View>

        {/* Challenges */}
        <View style={styles.statCard}>
          <Text style={styles.statValue}>{tripStats.challengesCompleted}</Text>
          <Text style={styles.statLabel}>Challenges</Text>
          <Text style={styles.statDetail}>{tripStats.challengesEligible} prize eligible</Text>
        </View>

        {/* Actividades */}
        <View style={styles.statCard}>
          <Text style={styles.statValue}>{tripStats.activitiesCompleted || 0}</Text>
          <Text style={styles.statLabel}>Activities</Text>
          <Text style={styles.statDetail}>of {tripStats.activitiesPlanned || 0} planned</Text>
        </View>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          <Text style={styles.wayfareBrand}>WAYFARE</Text> by LOS DIAS • www.losdias.xyz
        </Text>
        <Text style={styles.pageNumber}>Page 2</Text>
      </View>
    </Page>
  );
};

// Página de Información de Ciudad
const DestinationPage = ({ userData, cityData }) => {
  return (
    <Page size="A4" style={styles.pageWithPadding}>
      {/* Header */}
      <View style={styles.sectionHeader}>
        <View>
          <Text style={styles.sectionTitle}>About {userData.destination}</Text>
          <Text style={styles.sectionSubtitle}>Destination insights</Text>
        </View>
      </View>

      {/* Información de ciudad */}
      {cityData && (
        <View style={styles.cityInfoBox}>
          {cityData.briefDescription && (
            <View style={styles.cityInfoRow}>
              <View style={styles.cityInfoContent}>
                <Text style={styles.cityInfoLabel}>About</Text>
                <Text style={styles.cityInfoText}>{cityData.briefDescription}</Text>
              </View>
            </View>
          )}

          {cityData.climate && (
            <View style={styles.cityInfoRow}>
              <View style={styles.cityInfoContent}>
                <Text style={styles.cityInfoLabel}>Climate</Text>
                <Text style={styles.cityInfoText}>{cityData.climate}</Text>
              </View>
            </View>
          )}

          {cityData.currency && (
            <View style={styles.cityInfoRow}>
              <View style={styles.cityInfoContent}>
                <Text style={styles.cityInfoLabel}>Currency</Text>
                <Text style={styles.cityInfoText}>{cityData.currency}</Text>
              </View>
            </View>
          )}

          {cityData.timezone && (
            <View style={styles.cityInfoRow}>
              <View style={styles.cityInfoContent}>
                <Text style={styles.cityInfoLabel}>Timezone</Text>
                <Text style={styles.cityInfoText}>
                  {typeof cityData.timezone === 'string' ? cityData.timezone :
                   `${cityData.timezone.standardTime || ''}`}
                </Text>
              </View>
            </View>
          )}

          {cityData.languagesSpoken && (
            <View style={styles.cityInfoRow}>
              <View style={styles.cityInfoContent}>
                <Text style={styles.cityInfoLabel}>Languages</Text>
                <Text style={styles.cityInfoText}>
                  {cityData.languagesSpoken.officialLanguage || 'Multiple languages'}
                </Text>
              </View>
            </View>
          )}
        </View>
      )}

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          <Text style={styles.wayfareBrand}>WAYFARE</Text> by LOS DIAS • www.losdias.xyz
        </Text>
        <Text style={styles.pageNumber}>Page 3</Text>
      </View>
    </Page>
  );
};

// Página de Timeline Día a Día
const DayTimelinePage = ({ day, dayNotes, dayReflection, pageNumber }) => {
  const dayMemories = dayNotes.filter(note => !note.isChallenge);
  const dayChallenges = dayNotes.filter(note => note.isChallenge);

  // Obtener fecha del primer note
  const dayDate = dayNotes.length > 0 && dayNotes[0].timestamp
    ? new Date(dayNotes[0].timestamp).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      })
    : '';

  // Foto destacada del día (primera foto encontrada)
  const featuredPhoto = dayNotes.find(note => note.photos && note.photos.length > 0)?.photos[0];

  return (
    <Page size="A4" style={styles.pageWithPadding}>
      {/* Header del día */}
      <View style={styles.dayHeader}>
        <View style={styles.dayHeaderContent}>
          <View>
            <Text style={styles.dayNumber}>DAY {day}</Text>
            <Text style={styles.dayDate}>{dayDate}</Text>
          </View>

          <View style={styles.dayStats}>
            <View style={styles.dayStatItem}>
              <Text style={styles.dayStatText}>{dayMemories.length} memories</Text>
            </View>
            <View style={styles.dayStatItem}>
              <Text style={styles.dayStatText}>{dayChallenges.length} challenges</Text>
            </View>
          </View>
        </View>
      </View>

      {/* Foto destacada del día */}
      {featuredPhoto && featuredPhoto.data && (
        <View style={{ marginBottom: 16, maxHeight: 250 }} wrap={false}>
          <Image
            src={featuredPhoto.data}
            style={{ ...styles.photoLarge, maxHeight: 200 }}
          />
          {featuredPhoto.caption && (
            <Text style={styles.photoCaption}>{featuredPhoto.caption}</Text>
          )}
        </View>
      )}

      {/* Memories del día */}
      {dayMemories.length > 0 && (
        <View style={styles.section}>
          {dayMemories.map((note, index) => (
            <View key={note.id || index} style={styles.contentCard}>
              <View style={styles.cardTitle}>
                <Text>{note.title}</Text>
              </View>

              <Text style={styles.cardContent}>
                {note.content?.substring(0, 300)}
                {note.content?.length > 300 ? '...' : ''}
              </Text>

              {note.timestamp && (
                <View style={styles.cardMeta}>
                  <View style={styles.cardMetaItem}>
                    <Text>{new Date(note.timestamp).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</Text>
                  </View>
                </View>
              )}

              {/* Fotos del note — filas de 3, proporción natural, sin cortes */}
              {note.photos && note.photos.length > 0 && (() => {
                const photos = note.photos;
                const count = photos.length;

                if (count === 1) {
                  return (
                    <View wrap={false} style={{ marginTop: 10 }}>
                      <Image src={photos[0].data} style={{ width: '100%', borderRadius: 6, border: `1px solid ${colors.accent}` }} />
                      {photos[0].caption && <Text style={styles.photoCaption}>{photos[0].caption}</Text>}
                    </View>
                  );
                }

                if (count === 2) {
                  return (
                    <View wrap={false} style={{ flexDirection: 'row', gap: 6, marginTop: 10, alignItems: 'flex-start' }}>
                      {photos.map((p, i) => (
                        <View key={i} style={{ width: '48%' }}>
                          <Image src={p.data} style={{ width: '100%', borderRadius: 6, border: `1px solid ${colors.accent}` }} />
                          {p.caption && <Text style={styles.photoCaption}>{p.caption}</Text>}
                        </View>
                      ))}
                    </View>
                  );
                }

                // 3+ fotos: filas de 3
                const rows = [];
                for (let i = 0; i < photos.length; i += 3) rows.push(photos.slice(i, i + 3));
                return (
                  <View style={{ marginTop: 10 }}>
                    {rows.map((row, rowIdx) => (
                      <View key={rowIdx} wrap={false} style={{ flexDirection: 'row', gap: 6, marginBottom: 6, alignItems: 'flex-start' }}>
                        {row.map((p, i) => (
                          <View key={i} style={{ width: row.length === 1 ? '100%' : row.length === 2 ? '48%' : '31%' }}>
                            <Image src={p.data} style={{ width: '100%', borderRadius: 6, border: `1px solid ${colors.accent}` }} />
                            {p.caption && <Text style={styles.photoCaption}>{p.caption}</Text>}
                          </View>
                        ))}
                      </View>
                    ))}
                  </View>
                );
              })()}
            </View>
          ))}
        </View>
      )}

      {/* Challenges del día */}
      {dayChallenges.length > 0 && (
        <View style={styles.section}>
          {dayChallenges.map((challenge, index) => (
            <View key={challenge.id || index} style={styles.challengeCard}>
              <View style={styles.challengeBadge}>
                <Text style={styles.challengeBadgeText}>
                  {challenge.challengeData?.submitted ? 'COMPLETED' : 'CHALLENGE'}
                </Text>
              </View>

              <View style={styles.cardTitle}>
                <Text>{challenge.title}</Text>
              </View>

              {challenge.content && (
                <Text style={styles.cardContent}>
                  {challenge.content.substring(0, 200)}
                  {challenge.content.length > 200 ? '...' : ''}
                </Text>
              )}

              {/* Challenge photos - displayed AFTER text */}
              {challenge.photos && challenge.photos.length > 0 && (
                <View style={{ marginTop: 12, maxHeight: 280 }} wrap={false}>
                  {challenge.photos.map((photo, photoIndex) => (
                    <View key={photoIndex} wrap={false} style={{ marginBottom: 10, maxHeight: 250 }}>
                      <Image
                        src={photo.data}
                        style={{ ...styles.photoLarge, maxHeight: 200 }}
                      />
                      {photo.caption && (
                        <Text style={styles.photoCaption}>
                          {photo.caption}
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              )}
            </View>
          ))}
        </View>
      )}

      {/* Reflexión del día (si existe) */}
      {dayReflection && (
        <View style={styles.reflectionCard}>
          <Text style={styles.reflectionDayTitle}>Daily Reflection</Text>

          {/* How did you feel today? */}
          {dayReflection.answers?.feeling && (
            <View>
              <Text style={styles.reflectionQuestion}>How did you feel today?</Text>
              <Text style={styles.reflectionAnswer}>{dayReflection.answers.feeling}</Text>
            </View>
          )}

          {/* What surprised you today? */}
          {dayReflection.answers?.surprise && (
            <View>
              <Text style={styles.reflectionQuestion}>What surprised you today?</Text>
              <Text style={styles.reflectionAnswer}>{dayReflection.answers.surprise}</Text>
            </View>
          )}

          {/* What are you thankful for? */}
          {dayReflection.answers?.gratitude && (
            <View>
              <Text style={styles.reflectionQuestion}>What are you thankful for?</Text>
              <Text style={styles.reflectionAnswer}>{dayReflection.answers.gratitude}</Text>
            </View>
          )}

          {/* What are your expectations for tomorrow? */}
          {dayReflection.answers?.expectations && (
            <View>
              <Text style={styles.reflectionQuestion}>What are your expectations for tomorrow?</Text>
              <Text style={styles.reflectionAnswer}>{dayReflection.answers.expectations}</Text>
            </View>
          )}
        </View>
      )}

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          <Text style={styles.wayfareBrand}>WAYFARE</Text> by LOS DIAS • www.losdias.xyz
        </Text>
        <Text style={styles.pageNumber}>Page {pageNumber}</Text>
      </View>
    </Page>
  );
};

// Página de Galería Fotográfica
const PhotoGalleryPage = ({ photos, pageNumber }) => {
  // Tomar máximo 9 fotos para la galería
  const galleryPhotos = photos.slice(0, 9);

  return (
    <Page size="A4" style={styles.pageWithPadding}>
      {/* Header */}
      <View style={styles.sectionHeader}>
        <View>
          <Text style={styles.sectionTitle}>Photo Gallery</Text>
          <Text style={styles.sectionSubtitle}>Best moments captured</Text>
        </View>
      </View>

      {/* Grid de fotos */}
      <View style={styles.photoGrid}>
        {galleryPhotos.map((photo, index) => (
          <View key={index} wrap={false} style={styles.photoWrapper}>
            <Image
              src={photo.data}
              style={styles.photo}
            />
            {photo.caption && (
              <Text style={styles.photoCaption}>
                {photo.caption.length > 30
                  ? photo.caption.substring(0, 30) + '...'
                  : photo.caption}
              </Text>
            )}
          </View>
        ))}
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          <Text style={styles.wayfareBrand}>WAYFARE</Text> by LOS DIAS • www.losdias.xyz
        </Text>
        <Text style={styles.pageNumber}>Page {pageNumber}</Text>
      </View>
    </Page>
  );
};

// Página de Reflexiones Diarias
const ReflectionsPage = ({ reflections, pageNumber }) => {
  if (!reflections || reflections.length === 0) return null;

  // Ordenar reflexiones por día
  const sortedReflections = [...reflections].sort((a, b) => a.tripDay - b.tripDay);

  return (
    <Page size="A4" style={styles.pageWithPadding}>
      {/* Header */}
      <View style={styles.sectionHeader}>
        <View>
          <Text style={styles.sectionTitle}>Daily Reflections</Text>
          <Text style={styles.sectionSubtitle}>Your thoughts throughout the journey</Text>
        </View>
      </View>

      {/* Reflections */}
      {sortedReflections.map((reflection, index) => (
        <View key={reflection.id || index} style={styles.reflectionCard}>
          <Text style={styles.reflectionDayTitle}>Day {reflection.tripDay}</Text>

          {/* How did you feel today? */}
          {reflection.answers?.feeling && (
            <View>
              <Text style={styles.reflectionQuestion}>How did you feel today?</Text>
              <Text style={styles.reflectionAnswer}>{reflection.answers.feeling}</Text>
            </View>
          )}

          {/* What surprised you today? */}
          {reflection.answers?.surprise && (
            <View>
              <Text style={styles.reflectionQuestion}>What surprised you today?</Text>
              <Text style={styles.reflectionAnswer}>{reflection.answers.surprise}</Text>
            </View>
          )}

          {/* What are you thankful for? */}
          {reflection.answers?.gratitude && (
            <View>
              <Text style={styles.reflectionQuestion}>What are you thankful for?</Text>
              <Text style={styles.reflectionAnswer}>{reflection.answers.gratitude}</Text>
            </View>
          )}

          {/* What are your expectations for tomorrow? */}
          {reflection.answers?.expectations && (
            <View>
              <Text style={styles.reflectionQuestion}>What are your expectations for tomorrow?</Text>
              <Text style={styles.reflectionAnswer}>{reflection.answers.expectations}</Text>
            </View>
          )}
        </View>
      ))}

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          <Text style={styles.wayfareBrand}>WAYFARE</Text> by LOS DIAS • www.losdias.xyz
        </Text>
        <Text style={styles.pageNumber}>Page {pageNumber}</Text>
      </View>
    </Page>
  );
};

// Página de Highlights
const HighlightsPage = ({ tripData, pageNumber }) => {
  // Top 5 memories por popularidad o fecha
  const topMemories = tripData.notes
    ?.filter(note => !note.isChallenge)
    .slice(0, 5) || [];

  // Challenges completados
  const completedChallenges = tripData.notes
    ?.filter(note => note.isChallenge && note.challengeData?.submitted)
    .slice(0, 3) || [];

  return (
    <Page size="A4" style={styles.pageWithPadding}>
      {/* Header */}
      <View style={styles.sectionHeader}>
        <View>
          <Text style={styles.sectionTitle}>Trip Highlights</Text>
          <Text style={styles.sectionSubtitle}>Best moments of your journey</Text>
        </View>
      </View>

      {/* Top Memories */}
      {topMemories.length > 0 && (
        <View style={styles.highlightBox}>
          <Text style={styles.highlightTitle}>TOP MEMORIES</Text>
          <View style={styles.highlightList}>
            {topMemories.map((memory, index) => (
              <View key={memory.id || index} style={styles.highlightItem}>
                <Text style={styles.highlightBullet}>•</Text>
                <Text style={styles.highlightText}>{memory.title}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Completed Challenges */}
      {completedChallenges.length > 0 && (
        <View style={styles.highlightBox}>
          <Text style={styles.highlightTitle}>COMPLETED CHALLENGES</Text>
          <View style={styles.highlightList}>
            {completedChallenges.map((challenge, index) => (
              <View key={challenge.id || index} style={styles.highlightItem}>
                <Text style={styles.highlightBullet}>•</Text>
                <Text style={styles.highlightText}>{challenge.title}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          <Text style={styles.wayfareBrand}>WAYFARE</Text> by LOS DIAS • www.losdias.xyz
        </Text>
        <Text style={styles.pageNumber}>Page {pageNumber}</Text>
      </View>
    </Page>
  );
};

// COMPONENTE PRINCIPAL
export default function TripReportPDF_Magazine({ userData, tripStats, tripData, cityData }) {
  // Obtener foto destacada (primera foto disponible)
  const featuredPhoto = tripData.photos && tripData.photos.length > 0
    ? tripData.photos[0].data
    : null;

  // Agrupar notes por día
  const notesByDay = {};
  if (tripData.notes && tripData.notes.length > 0) {
    tripData.notes.forEach(note => {
      const day = note.tripDay || 1;
      if (!notesByDay[day]) {
        notesByDay[day] = [];
      }
      notesByDay[day].push(note);
    });
  }

  // Agrupar reflections por día
  const reflectionsByDay = {};
  if (tripData.reflections && tripData.reflections.length > 0) {
    tripData.reflections.forEach(reflection => {
      const day = reflection.tripDay || 1;
      reflectionsByDay[day] = reflection;
    });
  }

  let currentPage = 4; // Portada, Resumen, Destino = 3 páginas

  return (
    <Document>
      {/* Portada */}
      <CoverPage
        userData={userData}
        tripStats={tripStats}
        featuredPhoto={featuredPhoto}
      />

      {/* Página de Resumen */}
      <SummaryPage
        tripStats={tripStats}
        userData={userData}
      />

      {/* Página de Destino */}
      <DestinationPage
        userData={userData}
        cityData={cityData}
      />

      {/* Páginas de Timeline día a día (con reflections incluidas) */}
      {Object.keys(notesByDay)
        .sort((a, b) => Number(a) - Number(b))
        .map(day => {
          const dayNotes = notesByDay[day];
          const dayReflection = reflectionsByDay[day] || null;
          const page = currentPage++;
          return (
            <DayTimelinePage
              key={day}
              day={Number(day)}
              dayNotes={dayNotes}
              dayReflection={dayReflection}
              pageNumber={page}
            />
          );
        })}

      {/* Página de Highlights */}
      {tripData.notes && tripData.notes.length > 0 && (
        <HighlightsPage
          tripData={tripData}
          pageNumber={currentPage}
        />
      )}
    </Document>
  );
}
